import { Injectable } from '@angular/core';
import { Observable, map, switchMap, tap, take, of, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { Commande } from '../models/commande.model';

interface StatutCommande {
  id: number;
  nom: string;
  ordre: number;
}

@Injectable({
  providedIn: 'root'
})
export class CommandeService {
  private statutsMap: Map<number, number> = new Map(); // Map ordre -> id
  private statutsLoaded = false;
  private statutsLoading$: Observable<StatutCommande[]> | null = null;

  constructor(private api: ApiService) {
    this.loadStatuts();
  }

  private loadStatuts(): void {
    if (this.statutsLoaded || this.statutsLoading$) return;

    this.statutsLoading$ = this.api.get<StatutCommande[]>('/labase/statutcommandes').pipe(
      tap(statuts => {
        statuts.forEach(statut => {
          this.statutsMap.set(statut.ordre, statut.id);
        });
        this.statutsLoaded = true;
        this.statutsLoading$ = null;
      })
    );

    this.statutsLoading$.subscribe({
      error: (err) => {
        console.error('[CommandeService] Erreur chargement statuts:', err);
        this.statutsLoading$ = null;
      }
    });
  }

  private ensureStatutsLoaded(): Observable<void> {
    if (this.statutsLoaded) {
      return of(void 0);
    }

    if (this.statutsLoading$) {
      return this.statutsLoading$.pipe(map(() => void 0), take(1));
    }

    this.loadStatuts();
    return this.statutsLoading$!.pipe(map(() => void 0), take(1));
  }

  getAllCommandes(): Observable<Commande[]> {
    return this.api.get<Commande[]>('/labase/commandes');
  }

  getCommandeById(id: string): Observable<Commande> {
    return this.api.get<Commande>(`/labase/commandes/${id}`);
  }

  getCommandesByUser(userId: string): Observable<Commande[]> {
    return this.api.get<Commande[]>(`/labase/commandes/user/${userId}`);
  }

  updateCommandeStatus(id: string, statutOrdre: number | string, motifAnnulation?: string): Observable<Commande> {
    return this.ensureStatutsLoaded().pipe(
      switchMap(() => {
        // Convertir statutOrdre en nombre si c'est une string (vient du select HTML)
        const ordreNumber = typeof statutOrdre === 'string' ? parseInt(statutOrdre, 10) : statutOrdre;
        // Convertir statutOrdre en statutcommandeId
        const statutcommandeId = this.statutsMap.get(ordreNumber);
        if (!statutcommandeId) {
          return throwError(() => new Error(`Statut avec ordre ${ordreNumber} non trouvé dans le mapping`));
        }
        // Inclure motifAnnulation si fourni
        const body: any = { statutcommandeId };
        if (motifAnnulation) {
          body.motifAnnulation = motifAnnulation;
        }
        return this.api.put<Commande>(`/labase/commandes/${id}/status`, body);
      })
    );
  }

  /**
   * Appelle le webhook n8n pour imprimer le ticket de commande
   */
  printCommande(id: string): Observable<any> {
    return this.api.post<any>(`/labase/commandes/${id}/print`, {});
  }
}
